import type { PageParams, SortSpec } from '@smartsash/types';
import { FILTER_OPERATORS } from '@smartsash/ui';
import { z } from 'zod';

import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from './config';

export const sortSpecSchema = z.object({
  field: z.string().min(1),
  dir: z.enum(['asc', 'desc']),
});

/**
 * One row of the filter builder as it travels in `?filters=` (see `useTableFilterConditions`).
 *
 * The operator list comes from the builder's own tuple. A schema that lists fewer silently strips
 * a valid row on the next navigation, which is how a date range applied by the user disappeared
 * without a trace: `z.object` drops what it cannot parse, and the URL is the only place the
 * applied set is kept.
 */
export const filterConditionSchema = z.object({
  field: z.string().min(1),
  operator: z.enum(FILTER_OPERATORS),
  values: z.array(z.string()),
});

export const listSearchSchema = z.object({
  page: z.coerce.number().int().positive().catch(DEFAULT_PAGE),
  pageSize: z.coerce.number().int().positive().catch(DEFAULT_PAGE_SIZE),
  sort: sortSpecSchema.optional().catch(undefined),
  q: z.string().trim().optional().catch(undefined),
});

export type ListSearch = PageParams & Record<string, unknown>;

export function createListSearchSchema(): typeof listSearchSchema;
export function createListSearchSchema<const TShape extends z.ZodRawShape>(
  shape: TShape,
): z.ZodObject<typeof listSearchSchema.shape & TShape>;
export function createListSearchSchema(shape?: z.ZodRawShape) {
  return z.object({ ...listSearchSchema.shape, ...shape });
}

export function toTableSort(sort: SortSpec | undefined) {
  return sort == null ? null : { columnId: sort.field, direction: sort.dir };
}

export function toSortSpec(columnId: string, direction: 'asc' | 'desc'): SortSpec {
  return { field: columnId, dir: direction };
}
